import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Box } from "@mui/material";

// text format
import Rabbit from "rabbit-node/lib/rabbit";

/** REUSABLE COMPONENTS */
import CategoryTitle from "../../../reusable/categoryTitle/CategoryTitle";
import CategorySeeAllButton from "../../../reusable/categorySeeAll/CategorySeeAll";

// styles
import styles from "./Card.module.css";
import ColorThief from "colorthief";

const AudioCard = ({
  data = [],
  title = "",
  category_id = null,
  category_type = null,
}) => {
  const navigate = useNavigate();
  
  // ✅ Move all hooks to the top level - BEFORE any conditionals
  const [dominantColors, setDominantColors] = useState(null);
  const [gradientStyle, setGradientStyle] = useState('bg-gradient-to-br from-emerald-500 via-teal-600 to-green-800');

  // ✅ useEffect at top level with conditional logic inside
  useEffect(() => {
    // Only run for OneBook category
    if ((title === "OneBook" || category_id === 24) && data && data.length > 0) {
      const item = data[0];
      
      const extractColors = async () => {
        console.log("reach extract color");
        try {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.src = item.image;
          
          img.onload = () => {
            const colorThief = new ColorThief();
            const palette = colorThief.getPalette(img, 3);
            
            if (palette && palette.length >= 3) {
              const [color1, color2, color3] = palette;
              const gradient = `linear-gradient(135deg, 
                rgb(${color1[0]}, ${color1[1]}, ${color1[2]}), 
                rgb(${color2[0]}, ${color2[1]}, ${color2[2]}), 
                rgb(${color3[0]}, ${color3[1]}, ${color3[2]}))`;
              
              setGradientStyle('');
              console.log('gradient color');
              console.log(gradient);
              setDominantColors(gradient);
            }
          };
        } catch (error) {
          console.error('Error extracting colors:', error);
        }
      };

      if (item.image) {
        console.log("have image");
        extractColors();
      }
    }
  }, [title, category_id, data]); // Dependencies for useEffect

  const handleClick = (item) => {
    // Handle navigation based on category_type
    if (category_type === 'series') {
      navigate(`/audio/book-series/${item.item_id}`);
    } else if (category_type === 'author') {
      navigate(`books-by-author/${item.item_id}`);
    } else if (category_type === 'collection') {
      navigate(`/audio/books-by-collection/${item.item_id}`);
    } else {
      // Default fallback
      navigate(`/audio/book-series/${item.item_id}`);
    }
  };

  // Early return if no data or empty data
  if (!data || !Array.isArray(data) || data.length === 0) {
    // console.log("AudioCard - No data to display");
    return null;
  }

  // Special layout for OneBook category
  if (title === "OneBook" || category_id === 25   ) {
    const item = data[0];
  
    return (
      <Box className="w-full mt-2 px-4 py-20">        
        <div 
          onClick={() => handleClick(item)}
          className={`${dominantColors ? '' : gradientStyle} rounded-3xl p-6 cursor-pointer shadow-2xl min-h-[500px] relative overflow-visible`}
          style={dominantColors ? { background: dominantColors } : {}}
        >
          <div className="absolute -top-14 w-24 h-32 z-1">
            <img
              src={item.image}
              alt={item.title}
              className="w-full h-full object-cover rounded-lg shadow-lg"
            />
          </div>
      
          <div className="mt-16 px-2">
            <h6 className="text-white font-bold mb-2 leading-tight">
              {item.title}
            </h6>
            {item.description && (
              <div className="text-white text-sm leading-relaxed text-justify">
                <p className="whitespace-pre-line">
                  {item.description}
                </p>
              </div>
            )}
          </div>
          
          <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -translate-y-16 translate-x-16"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full translate-y-12 -translate-x-12"></div>
        </div>
      </Box>
    );
  }
  
  return (
    <Box className="w-full mt-2">
      <div className="my-4 flex items-center">
        <CategoryTitle title={title} />
        {category_id && category_type && (
          <CategorySeeAllButton to={`/audio/see_all/${category_id}/${category_type}`} />
        )}
      </div>
  
      <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide">
        {data.map((item, index) => (
          <div key={index} className={`flex-shrink-0 ${category_type === 'collection' ? 'w-60' : category_type === 'author' ? 'w-20' : 'w-28'} py-2 h-auto`}>
            {category_type === 'author' ? (
              // Author layout: circular image with text below
              <div
                onClick={() => handleClick(item)}
                className="flex flex-col items-center cursor-pointer"
              >
                <div className="relative w-16 h-16 mb-2">
                  <img
                    src={item.image}
                    alt={item.title || `Author ${index + 1}`}
                    className="w-full h-full object-cover rounded-full"
                  />
                </div>
                {item.title && (
                  <span className="text-center text-xs text-white truncate w-full">
                    {item.title}
                  </span>
                )}
              </div>
            ) : (
              // Default layout: rectangular image with overlay text
              <div
                onClick={() => handleClick(item)}
                className="relative rounded-lg overflow-hidden cursor-pointer"
              >
                <img
                  src={item.image}
                  alt={item.title || `Image ${index + 1}`}
                  className="w-full h-auto"
                  style={{ textShadow: "2px 2px 4px #000000" }}
                />
                {item.title && (
                  <span className="text-center text-xs text-white truncate w-full mt-1 block">
                    {item.title}
                  </span>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </Box>
  );
}

export default AudioCard;